import { AutoGraph } from "@mui/icons-material";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import Component from "components/Component";
import AdminDashboard from "components/admin/AdminDashboard";
import { RunStatusChip } from "components/admin/DemoData";
import DemoRunProgress from "components/admin/DemoRunProgress";
import DashboardContent from "components/layout/DashboardContent";
import { Link } from "react-router-dom";
import { useListAllActiveDemoRunsQuery } from "services/adminService";

function parseRunId(runId) {
  // Format: 20260703T103954Z or 20260703T103954Z_gen
  const ts = runId.replace(/_gen$/, "");
  if (ts.length < 15) return null;
  try {
    return new Date(
      `${ts.slice(0, 4)}-${ts.slice(4, 6)}-${ts.slice(6, 8)}T${ts.slice(
        9,
        11
      )}:${ts.slice(11, 13)}:${ts.slice(13, 15)}Z`
    );
  } catch {
    return null;
  }
}

function elapsed(date) {
  if (!date) return null;
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
}

function RunCard({ run }) {
  const started = parseRunId(run.run_id);
  return (
    <Box
      sx={{
        p: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        background: "white",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <RunStatusChip step={run.step} />
        <Typography
          component={Link}
          to={`/admin/${run.company_id}/demo-data`}
          variant={"body2"}
          sx={{
            fontWeight: 600,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          {run.company_id}
        </Typography>
        <Typography variant={"body2"} sx={{ color: "text.secondary", flex: 1 }}>
          {started
            ? `${started.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })} · ${elapsed(started)}`
            : run.run_id}
        </Typography>
      </Box>
      {run.step === "running" && <DemoRunProgress progress={run.progress} />}
    </Box>
  );
}

function Section({ title, icon, runs }) {
  return (
    <Box>
      <Stack
        direction={"row"}
        alignItems={"center"}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        {icon}
        <Typography variant={"subtitle2"} color={"text.secondary"}>
          {title}
          {runs.length > 0 && (
            <Typography component={"span"} variant={"caption"} sx={{ ml: 1 }}>
              {`(${runs.length})`}
            </Typography>
          )}
        </Typography>
      </Stack>
      {runs.length === 0 ? (
        <Typography variant={"body2"} color={"text.disabled"}>
          {"None running"}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {runs.map((r) => (
            <RunCard key={r.run_id} run={r} />
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default function DemoDataProgress() {
  const { data, isFetching, refetch } = useListAllActiveDemoRunsQuery(
    undefined,
    { pollingInterval: 20000 }
  );

  const runs = data?.runs || [];
  const profileRuns = runs.filter((r) => r.run_type === "profile");
  const genRuns = runs.filter((r) => r.run_type === "gen");

  return (
    <AdminDashboard>
      <DashboardContent isFullWidth={false}>
        <Component
          title={"Demo Data Progress"}
          isFetching={isFetching}
          options={
            <Button size={"small"} onClick={refetch} disabled={isFetching}>
              {"Refresh"}
            </Button>
          }
        >
          <Stack spacing={3}>
            <Section
              title={"Profiling"}
              icon={<AutoGraph fontSize={"small"} color={"action"} />}
              runs={profileRuns}
            />
            <Divider />
            <Section
              title={"Generating"}
              icon={<AutoGraph fontSize={"small"} color={"action"} />}
              runs={genRuns}
            />
          </Stack>
        </Component>
      </DashboardContent>
    </AdminDashboard>
  );
}
